import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, User, Bell } from 'lucide-react';
import { ChatMessage } from '../../types/game.types';

interface ChatWidgetProps {
    messages: ChatMessage[];
    onSendMessage: (message: string) => void;
    currentUserId?: string;
    unreadCount: number;
    onOpen: () => void;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({
    messages,
    onSendMessage,
    currentUserId,
    unreadCount,
    onOpen
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
            onOpen(); // Helper to clear unread
        }
    }, [messages, isOpen, onOpen]);

    const handleSend = (e?: React.FormEvent) => {
        e?.preventDefault();
        if (newMessage.trim()) {
            onSendMessage(newMessage.trim());
            setNewMessage('');
            setTimeout(scrollToBottom, 100);
        }
    };

    const toggleChat = () => {
        setIsOpen(!isOpen);
    };

    return (
        <>
            {/* Toggle Button */}
            <motion.button
                onClick={toggleChat}
                className="fixed bottom-6 right-6 z-50 p-4 bg-black/80 backdrop-blur border border-amber-500/50 text-amber-500 rounded-full shadow-[0_0_15px_rgba(251,191,36,0.2)] hover:shadow-[0_0_25px_rgba(251,191,36,0.4)] hover:bg-amber-950/30 transition-all group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
            >
                <div className="relative">
                    <MessageSquare size={24} />
                    {unreadCount > 0 && !isOpen && (
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute -top-3 -right-3 bg-red-600 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-sm border border-black shadow-[0_0_5px_#ef4444]"
                        >
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </motion.div>
                    )}
                </div>
            </motion.button>

            {/* Chat Window */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.9 }}
                        className="fixed bottom-24 right-6 w-80 md:w-96 z-50 bg-slate-950/95 backdrop-blur-md border border-amber-500/30 rounded flex flex-col overflow-hidden h-[500px] shadow-[0_0_30px_rgba(0,0,0,0.8)]"
                    >
                        {/* Header */}
                        <div className="p-3 border-b border-amber-500/30 flex justify-between items-center bg-black/60">
                            <div className="flex items-center gap-2 text-amber-500 font-mono text-sm tracking-wider uppercase">
                                <MessageSquare size={16} />
                                <span>COM_LINK_SECURE</span>
                            </div>
                            <button
                                onClick={toggleChat}
                                className="text-slate-500 hover:text-amber-500 transition-colors"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Messages Area */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin scrollbar-thumb-amber-900/50 scrollbar-track-transparent font-sans">
                            {messages.length === 0 ? (
                                <div className="text-center text-slate-600 text-xs font-mono mt-10 uppercase tracking-widest">
                                    -- Signal Établi --<br />Aucune transmission
                                </div>
                            ) : (
                                messages.map((msg) => {
                                    const isMe = msg.senderId === currentUserId;
                                    return (
                                        <div
                                            key={msg.id}
                                            className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}
                                        >
                                            <div className={`flex items-end gap-2 max-w-[90%] ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                                                {/* Avatar Placeholder */}
                                                {!isMe && (
                                                    <div className="w-6 h-6 rounded bg-slate-900 border border-slate-700 flex items-center justify-center shrink-0">
                                                        <User size={12} className="text-slate-500" />
                                                    </div>
                                                )}

                                                <div className={`p-2.5 text-sm rounded-sm backdrop-blur-sm border ${isMe
                                                    ? 'bg-amber-950/40 border-amber-500/30 text-amber-100'
                                                    : 'bg-slate-900/60 border-slate-700/50 text-slate-300'
                                                    }`}>
                                                    {!isMe && <div className="text-[10px] text-cyan-500 font-mono mb-1 uppercase opacity-80">{msg.senderName}</div>}
                                                    {msg.message}
                                                </div>
                                            </div>
                                            <span className="text-[9px] text-slate-600 mt-1 px-1 font-mono">
                                                {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                    );
                                })
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input Area */}
                        <form onSubmit={handleSend} className="p-3 border-t border-amber-500/30 bg-black/40">
                            <div className="relative flex items-center group">
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    placeholder="Transmettre..."
                                    className="w-full bg-slate-900/50 border border-slate-700 text-amber-50 rounded-sm py-2 pl-3 pr-10 text-sm focus:outline-none focus:border-amber-500/50 focus:bg-slate-900/80 placeholder:text-slate-600 font-mono transition-colors"
                                />
                                <button
                                    type="submit"
                                    disabled={!newMessage.trim()}
                                    className="absolute right-1 p-1.5 text-amber-500 hover:text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                >
                                    <Send size={16} />
                                </button>
                            </div>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default ChatWidget;
